/* eslint-disable @typescript-eslint/no-explicit-any */
import { VariantProps, cva } from 'class-variance-authority';
import { ForwardedRef, HTMLAttributes, ReactNode, forwardRef, useMemo } from 'react';

import { Loader } from '../Loader';
import { Link, LinkRouteMapping } from '../Link';
import { WithTooltip, withToolTip } from '../Tooltip';

import { Color, cn } from 'styles';
import { pick } from 'utils/object';

type ButtonColor =
  | Extract<
      Color,
      'primary' | 'secondary' | 'success' | 'destructive' | 'info' | 'warning' | 'border'
    >
  | 'gray';

const buttonVariants = cva(
  [
    `relative inline-flex items-center justify-center gap-2 whitespace-nowrap`,
    'border',
    'transition-colors disabled:pointer-events-none disabled:cursor-default',
    'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
  ].join(' '),
  {
    variants: {
      size: {
        fit: 'text-base',
        small: 'text-sm',
        medium: 'text-sm',
        large: 'text-sm',
      },
      format: {
        rectangle: '',
        square: '',
      },
      variant: {
        filled: 'border-transparent font-semibold',
        outlined: 'font-medium',
        ghost: 'border-transparent font-medium',
        link: 'border-transparent font-medium underline-offset-4 hover:underline',
      },
    },

    defaultVariants: {
      size: 'small',
      format: 'rectangle',
      variant: 'filled',
    },

    compoundVariants: [
      {
        format: ['square'],
        size: ['fit'],
        className: 'p-0',
      },
      {
        format: ['square'],
        size: ['small'],
        className: 'p-1',
      },
      {
        format: ['square'],
        size: ['medium'],
        className: 'p-2',
      },
      {
        format: ['square'],
        size: ['large'],
        className: 'p-3',
      },
      {
        format: ['rectangle'],
        size: ['fit'],
        variant: ['filled', 'outlined', 'ghost'],
        className: 'h-auto px-2',
      },
      {
        format: ['rectangle'],
        size: ['small'],
        variant: ['filled', 'outlined', 'ghost'],
        className: 'h-8 px-3',
      },
      {
        format: ['rectangle'],
        size: ['medium'],
        variant: ['filled', 'outlined', 'ghost'],
        className: 'h-9 px-4 py-2',
      },
      {
        format: ['rectangle'],
        size: ['large'],
        variant: ['filled', 'outlined', 'ghost'],
        className: 'h-10 px-8',
      },
    ],
  },
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

type ButtonHTMLProps = Pick<
  HTMLAttributes<HTMLButtonElement>,
  'onClick' | 'title' | 'id' | 'className' | 'draggable'
>;

type SeparateLinkOptions = LinkRouteMapping[keyof LinkRouteMapping];

type InternalOptions = SeparateLinkOptions & {
  linkTarget?: string;
  type?: never;
  href?: never;
};

type ButtonLinkProps =
  | InternalOptions
  | {
      to?: never;
      type?: never;
      linkTarget?: string;
      href: string;
    }
  | {
      type?: HTMLButtonElement['type'];
      linkTarget?: never;
      href?: never;
      to?: never;
    };

export type ButtonProps = ButtonHTMLProps &
  ButtonVariantProps &
  ButtonLinkProps &
  WithTooltip<{
    children: ReactNode;

    color?: ButtonColor;
    round?: boolean;

    loading?: boolean;
    loadingSize?: number;

    disabled?: boolean;
  }>;

type VariantColorConfig = {
  [Variant in NonNullable<ButtonVariantProps['variant']>]: {
    [VarCor in ButtonColor]: string;
  };
};

const variantColorConfig: VariantColorConfig = {
  filled: {
    primary: 'bg-primary text-primary-foreground focus-visible:ring-primary hover:bg-primary-dark',
    secondary:
      'bg-secondary text-secondary-foreground focus-visible:ring-secondary hover:bg-secondary-dark',
    success: 'bg-success text-success-foreground focus-visible:ring-success hover:bg-success-dark',
    destructive:
      'bg-destructive text-destructive-foreground focus-visible:ring-destructive hover:bg-destructive-dark',
    info: 'bg-info text-info-foreground focus-visible:ring-info hover:bg-info-dark',
    warning: 'bg-warning text-warning-foreground focus-visible:ring-warning hover:bg-warning-dark',
    border: 'bg-border text-white focus-visible:ring-primary hover:bg-border-dark',
    gray: '!border-border bg-muted/50 text-muted-foreground focus-visible:ring-hover',
  },

  outlined: {
    primary:
      'border-primary text-primary focus-visible:ring-primary hover:border-primary hover:bg-primary/5',

    secondary:
      'border-secondary text-secondary focus-visible:ring-secondary hover:border-secondary hover:bg-secondary/5',

    success:
      'border-success text-success focus-visible:ring-success hover:border-success hover:bg-success/5',

    destructive:
      'border-destructive text-destructive focus-visible:ring-destructive hover:border-destructive hover:bg-destructive/5',

    info: 'border-info text-info focus-visible:ring-info hover:border-info hover:bg-info/5',

    warning:
      'border-warning text-warning focus-visible:ring-warning hover:border-warning hover:bg-warning/5',

    border: 'border-border text-muted-foreground focus-visible:ring-primary hover:bg-hover',

    gray: 'border-input text-foreground focus-visible:ring-input hover:bg-muted/40',
  },

  ghost: {
    primary: 'focus-visible-transparent hover:bg-hover text-primary',
    secondary: 'focus-visible-transparent hover:bg-hover text-secondary',
    success: 'focus-visible-transparent hover:bg-hover text-success',
    destructive: 'focus-visible-transparent hover:bg-hover text-destructive',
    info: 'focus-visible-transparent hover:bg-hover text-info',
    warning: 'focus-visible-transparent hover:bg-hover text-warning',
    border: 'focus-visible-transparent hover:bg-hover text-border',
    gray: 'focus-visible-transparent hover:bg-hover text-muted-foreground',
  },

  link: {
    primary: 'text-primary focus-visible:ring-transparent',
    secondary: 'text-secondary focus-visible:ring-transparent',
    success: 'text-success focus-visible:ring-transparent',
    destructive: 'text-destructive focus-visible:ring-transparent',
    info: 'text-info focus-visible:ring-transparent',
    warning: 'text-warning focus-visible:ring-transparent',
    border: 'text-foreground focus-visible:ring-transparent',
    gray: 'text-muted-foreground focus-visible:ring-transparent',
  },
};

function resolveColorClasses(
  color: ButtonColor,
  variant: NonNullable<ButtonVariantProps['variant']>,
): string {
  return variantColorConfig[variant]?.[color] ?? '';
}

function getDefaultColor({ color, variant }: Pick<ButtonProps, 'color' | 'variant'>) {
  if (variant === 'outlined') return color ?? 'gray';

  return color ?? 'primary';
}

const ButtonComponent = forwardRef(
  (
    {
      children,
      className,
      size,
      color = 'primary',
      format = 'rectangle',
      variant = 'filled',
      round = false,
      type = 'button',
      loading = false,
      loadingSize,
      disabled = false,
      onClick,

      href,
      linkTarget,
      to,

      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const typeProperties = useMemo(
      () =>
        pick(
          {
            type,

            to: to ?? href!,
            target: linkTarget,
          },
          href || to ? ['to', 'target'] : ['type'],
        ),
      [href, linkTarget, type, to],
    );

    const Component = useMemo(() => (href || to ? Link : 'button'), [href, to]);

    const defaultColor = getDefaultColor({ color, variant });

    return (
      <Component
        {...(props as any)}
        {...typeProperties}
        ref={ref as any}
        disabled={loading || disabled}
        onClick={loading || disabled ? undefined : onClick}
        className={cn(
          round ? 'rounded-full' : 'rounded-lg',
          disabled && 'cursor-default disabled:opacity-50',
          resolveColorClasses(defaultColor, variant!),
          buttonVariants({ className, size, format, variant }),
        )}
      >
        <span
          className={cn('w-full transition-opacity', loading && 'opacity-0')}
          style={{
            alignItems: 'inherit',
            justifyContent: 'inherit',
            gap: 'inherit',
            display: 'inherit',
          }}
        >
          {children}
        </span>

        {loading && <Loader inheritColor className="absolute" size={loadingSize} />}
      </Component>
    );
  },
);

ButtonComponent.displayName = 'Button';

export const Button = withToolTip(ButtonComponent, { hasRef: true });
